// API Service for IntaStream Portal - TypeScript Version
// Converted from JavaScript with full type safety

import {
  LoginResponse,
  AuthStatusResponse,
  AppointmentCreateRequest,
  AppointmentUpdateRequest,
  AppointmentResponse,
  AppointmentsResponse,
  MedicalRolesResponse,
  ProvidersResponse,
  ProfessionalResponse,
  ProfessionalsResponse,
  TrainingCourseResponse,
  TrainingCoursesResponse,
  TrainingProgressRequest,
  TrainingProgressResponse,
  TrainingProgressListResponse,
  ConversationCreateRequest,
  ConversationResponse,
  ConversationsResponse,
  MessageSendRequest,
  MessageResponse,
  MessagesResponse,
  OnlineUsersResponse,
  ApiResult,
  AnyApiError,
  HttpMethod,
} from '../types';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://intastream.com/api' 
  : 'http://localhost:8080/api';

// JWT Token management - Now using httpOnly cookies for security
// Tokens are stored in httpOnly cookies and managed by the server
let isAuthenticated: boolean = false;

const setTokens = (token: string, refreshToken?: string): void => {
  // Tokens are now set as httpOnly cookies by the server
  // We just track authentication state for UI purposes
  isAuthenticated = true;
  
  // Clear any old localStorage tokens for security
  localStorage.removeItem('intastream_access_token');
  localStorage.removeItem('intastream_refresh_token');
};

const getToken = (): string | null => {
  // Tokens are now in httpOnly cookies, not accessible via JavaScript
  // This function exists for backward compatibility but returns null
  // Authentication is handled automatically via cookies
  return null;
};

const clearTokens = (): void => {
  // Clear authentication state
  isAuthenticated = false;
  
  // Clear any remaining localStorage tokens for security
  localStorage.removeItem('intastream_access_token');
  localStorage.removeItem('intastream_refresh_token');
  
  // Note: httpOnly cookies are automatically cleared by the logout endpoint
};

const isUserAuthenticated = (): boolean => {
  return isAuthenticated;
};

// Check if user is authenticated by testing a protected endpoint
const checkAuthenticationStatus = async (): Promise<AuthStatusResponse> => {
  try {
    // Make a simple authenticated request to verify cookie-based auth
    const response = await fetch(`${API_BASE_URL}/test-auth`, {
      method: 'GET',
      credentials: 'include', // Include httpOnly cookies
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (response.ok) {
      const authData = await response.json();
      isAuthenticated = true;
      return {
        authenticated: true,
        userID: authData.userID,
        userType: authData.userType,
        email: authData.userID, // For now, userID is the email
        providerId: authData.providerId
      };
    } else {
      isAuthenticated = false;
      return { authenticated: false };
    }
  } catch (error) {
    console.warn('Authentication check failed:', error);
    isAuthenticated = false;
    return { authenticated: false };
  }
};

// Utility function to handle API responses
async function handleApiResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // If token expired, clear tokens and throw authentication error
    if (response.status === 401) {
      clearTokens();
      throw new Error('Authentication required');
    }
    const errorText = await response.text();
    throw new Error(`API Error ${response.status}: ${errorText}`);
  }
  return response.json() as Promise<T>;
}

// Generic fetch wrapper with type safety
async function apiFetch<T>(
  endpoint: string,
  options: {
    method?: HttpMethod;
    headers?: Record<string, string>;
    body?: any;
    queryParams?: Record<string, string | number | boolean>;
  } = {}
): Promise<T> {
  const {
    method = 'GET',
    headers = {},
    body,
    queryParams,
  } = options;

  let url = `${API_BASE_URL}${endpoint}`;
  
  // Add query parameters if provided
  if (queryParams) {
    const params = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  // Authentication now handled automatically via httpOnly cookies
  // No need to manually add Authorization header
  // Cookies are sent automatically with each request

  // Remove Content-Type for FormData
  if (body instanceof FormData) {
    delete requestHeaders['Content-Type'];
  }

  const requestOptions: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: 'include', // Include httpOnly cookies in requests
  };

  if (body) {
    requestOptions.body = body instanceof FormData ? body : JSON.stringify(body);
  }

  const response = await fetch(url, requestOptions);
  return handleApiResponse<T>(response);
}

export const apiService = {
  // ============================================================================
  // TOKEN MANAGEMENT METHODS
  // ============================================================================
  
  setTokens,
  getToken,
  clearTokens,
  isUserAuthenticated,
  checkAuthenticationStatus,

  // ============================================================================
  // AUTHENTICATION METHODS
  // ============================================================================

  async authenticateProvider(username: string, password: string): Promise<LoginResponse> {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    
    return apiFetch<LoginResponse>('/auth', {
      method: 'POST',
      body: formData,
    });
  },

  async authenticateProfessional(username: string, password: string): Promise<LoginResponse> {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    
    return apiFetch<LoginResponse>('/auth/professional', {
      method: 'POST',
      body: formData,
    });
  },

  async logout(): Promise<{ message: string }> {
    const result = await apiFetch<{ message: string }>('/auth/logout', {
      method: 'POST',
    });
    
    // Clear authentication state after successful logout
    clearTokens();
    
    return result;
  },

  async checkAuthStatus(): Promise<{ authenticated: boolean; userID?: string; userType?: string; email?: string }> {
    return apiFetch<{ authenticated: boolean; userID?: string; userType?: string; email?: string }>('/auth/status');
  },

  // ============================================================================
  // APPOINTMENT METHODS
  // ============================================================================

  async getProviderAppointments(
    providerId: number, 
    startDate?: string, 
    endDate?: string
  ): Promise<AppointmentsResponse> {
    const queryParams: Record<string, string> = {};
    if (startDate) queryParams.startDate = startDate;
    if (endDate) queryParams.endDate = endDate;

    return apiFetch<AppointmentsResponse>(`/providers/${providerId}/appointments`, {
      queryParams,
    });
  },

  async getAppointmentDetails(appointmentId: number): Promise<AppointmentResponse> {
    return apiFetch<AppointmentResponse>(`/appointments/${appointmentId}`);
  },

  async createStaffRequest(
    providerId: number, 
    staffRequest: AppointmentCreateRequest
  ): Promise<AppointmentResponse> {
    console.log('API createStaffRequest called with:', { providerId, staffRequest });
    console.log('Full URL:', `${API_BASE_URL}/providers/${providerId}/appointments`);
    
    return apiFetch<AppointmentResponse>(`/providers/${providerId}/appointments`, {
      method: 'POST',
      body: staffRequest,
    });
  },

  async updateAppointment(
    appointmentId: number, 
    appointmentData: AppointmentUpdateRequest
  ): Promise<AppointmentResponse> {
    return apiFetch<AppointmentResponse>(`/appointments/${appointmentId}`, {
      method: 'PUT',
      body: appointmentData,
    });
  },

  async deleteAppointment(appointmentId: number): Promise<{ success: boolean }> {
    return apiFetch<{ success: boolean }>(`/appointments/${appointmentId}`, {
      method: 'DELETE',
    });
  },

  // ============================================================================
  // PROVIDER AND PROFESSIONAL METHODS
  // ============================================================================

  async getMedicalRoles(): Promise<MedicalRolesResponse> {
    return apiFetch<MedicalRolesResponse>('/medical-roles');
  },

  async getProviders(): Promise<ProvidersResponse> {
    return apiFetch<ProvidersResponse>('/providers');
  },

  async getProfessionals(): Promise<ProfessionalsResponse> {
    return apiFetch<ProfessionalsResponse>('/professionals');
  },

  async getProfessionalByNI(niNumber: string): Promise<ProfessionalResponse | null> {
    try {
      return await apiFetch<ProfessionalResponse>(`/professionals/ni/${niNumber}`);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null; // Professional not found
      }
      throw error;
    }
  },

  // ============================================================================
  // TRAINING METHODS
  // ============================================================================

  async getRequiredCoursesForProfessional(professionalId: number): Promise<TrainingCoursesResponse> {
    return apiFetch<TrainingCoursesResponse>(`/training/courses/professional/${professionalId}`);
  },

  async getCourseContent(courseId: number): Promise<TrainingCourseResponse> {
    try {
      return await apiFetch<TrainingCourseResponse>(`/training/courses/${courseId}`);
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        throw new Error('Course not found');
      }
      throw new Error('Failed to fetch course content');
    }
  },

  async saveTrainingProgress(
    professionalId: number,
    courseId: number,
    status: string,
    score?: number | null,
    sectionProgress?: any | null
  ): Promise<TrainingProgressResponse> {
    const request: TrainingProgressRequest = {
      professionalId,
      courseId,
      isCompleted: status === 'completed',
    };

    if (score !== null && score !== undefined) {
      request.score = score;
    }

    if (sectionProgress) {
      request.notes = JSON.stringify(sectionProgress);
    }

    return apiFetch<TrainingProgressResponse>('/training/progress', {
      method: 'POST',
      body: request,
    });
  },

  async getTrainingProgressForProfessional(professionalId: number): Promise<TrainingProgressListResponse> {
    return apiFetch<TrainingProgressListResponse>(`/training/progress/professional/${professionalId}`);
  },

  // ============================================================================
  // MESSAGING METHODS
  // ============================================================================

  async getConversations(userId: number): Promise<ConversationsResponse> {
    return apiFetch<ConversationsResponse>('/messaging/conversations', {
      headers: {
        'X-User-ID': String(userId),
      },
    });
  },

  async createConversation(
    userId: number,
    hospitalProviderId: number,
    subject?: string
  ): Promise<ConversationResponse> {
    const request: ConversationCreateRequest = {
      hospitalProviderId,
    };

    if (subject) {
      request.subject = subject;
    }

    return apiFetch<ConversationResponse>('/messaging/conversations', {
      method: 'POST',
      headers: {
        'X-User-ID': String(userId),
      },
      body: request,
    });
  },

  async getMessages(
    conversationId: number,
    pageSize: number = 50,
    pageNumber: number = 1
  ): Promise<MessagesResponse> {
    return apiFetch<MessagesResponse>(`/messaging/conversations/${conversationId}/messages`, {
      queryParams: { pageSize, pageNumber },
    });
  },

  async sendMessage(
    userId: number,
    conversationId: number,
    content: string,
    messageType: string = 'text'
  ): Promise<MessageResponse> {
    const request = {
      conversationId,
      content: content,
      messageType: messageType,
    };

    return apiFetch<MessageResponse>(`/messaging/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers: {
        'X-User-ID': String(userId),
      },
      body: request,
    });
  },

  async markAsRead(userId: number, conversationId: number): Promise<{ success: boolean }> {
    return apiFetch<{ success: boolean }>(`/messaging/conversations/${conversationId}/read`, {
      method: 'PUT',
      headers: {
        'X-User-ID': String(userId),
      },
    });
  },

  async setTypingStatus(
    userId: number,
    conversationId: number,
    isTyping: boolean
  ): Promise<{ success: boolean }> {
    return apiFetch<{ success: boolean }>('/messaging/typing', {
      method: 'POST',
      headers: {
        'X-User-ID': String(userId),
      },
      body: {
        conversationId,
        isTyping,
      },
    });
  },

  async getOnlineUsers(): Promise<OnlineUsersResponse> {
    return apiFetch<OnlineUsersResponse>('/messaging/online-users');
  },

  // ============================================================================
  // CERTIFICATE MANAGEMENT METHODS (Future Integration)
  // ============================================================================

  async getCertificate(certificateId: number): Promise<any> {
    return apiFetch<any>(`/training/certificates/${certificateId}`);
  },

  async getProfessionalCertificates(professionalId: number): Promise<any[]> {
    return apiFetch<any[]>(`/training/certificates/professional/${professionalId}`);
  },

  async storeCertificate(certificateData: {
    professionalId: number;
    courseId: number;
    certificateId: string;
    courseTitle: string;
    completionDate: string;
    expiryDate?: string;
    score?: number;
    filename: string;
  }): Promise<{ message: string }> {
    return apiFetch<{ message: string }>('/training/certificates', {
      method: 'POST',
      body: certificateData,
    });
  },

  async deleteCertificate(certificateId: number): Promise<{ success: boolean }> {
    return apiFetch<{ success: boolean }>(`/training/certificates/${certificateId}`, {
      method: 'DELETE',
    });
  },

  // ============================================================================
  // FIELD MAPPING METHODS (With Caching)
  // ============================================================================

  _fieldMappingsCache: null as any,
  _fieldMappingsCacheTime: 0,
  _CACHE_DURATION: 30000, // 30 seconds cache

  async getFieldMappings(): Promise<any> {
    const now = Date.now();
    
    // Return cached data if it's fresh
    if (this._fieldMappingsCache && (now - this._fieldMappingsCacheTime) < this._CACHE_DURATION) {
      console.log('Returning cached field mappings');
      return this._fieldMappingsCache;
    }
    
    console.log('Fetching fresh field mappings from API');
    const data = await apiFetch<any>('/field-mappings');
    
    // Cache the response
    this._fieldMappingsCache = data;
    this._fieldMappingsCacheTime = now;
    
    return data;
  },

  clearFieldMappingsCache(): void {
    this._fieldMappingsCache = null;
    this._fieldMappingsCacheTime = 0;
  },

  async saveFieldMapping(
    csvColumn: string,
    databaseColumn: string | null,
    notes: string | null,
    userEmail?: string
  ): Promise<any> {
    const headers: Record<string, string> = {};
    if (userEmail) {
      headers['X-User-Email'] = userEmail;
    }

    const result = await apiFetch<any>('/field-mappings', {
      method: 'POST',
      headers,
      body: {
        csvColumn,
        databaseColumn: databaseColumn || null,
        notes: notes || null,
      },
    });
    
    // Clear cache when data is updated
    this.clearFieldMappingsCache();
    
    return result;
  },

  async updateFieldMapping(
    mappingId: number,
    csvColumn: string,
    databaseColumn: string | null,
    notes: string | null,
    userEmail?: string
  ): Promise<any> {
    const headers: Record<string, string> = {};
    if (userEmail) {
      headers['X-User-Email'] = userEmail;
    }

    return apiFetch<any>(`/field-mappings/${mappingId}`, {
      method: 'PUT',
      headers,
      body: {
        csvColumn,
        databaseColumn: databaseColumn || null,
        notes: notes || null,
      },
    });
  },

  async deleteFieldMapping(mappingId: number, userEmail?: string): Promise<any> {
    const headers: Record<string, string> = {};
    if (userEmail) {
      headers['X-User-Email'] = userEmail;
    }

    return apiFetch<any>(`/field-mappings/${mappingId}`, {
      method: 'DELETE',
      headers,
    });
  },

  async getMappingConfigurations(): Promise<any[]> {
    return apiFetch<any[]>('/field-mappings/configurations');
  },

  async saveMappingConfiguration(
    configurationName: string,
    description: string | null,
    configurationData: any,
    userEmail?: string
  ): Promise<any> {
    const headers: Record<string, string> = {};
    if (userEmail) {
      headers['X-User-Email'] = userEmail;
    }

    return apiFetch<any>('/field-mappings/configurations', {
      method: 'POST',
      headers,
      body: {
        configurationName,
        description: description || null,
        configurationData: JSON.stringify(configurationData),
      },
    });
  },

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Generic method to make safe API calls with error handling
   */
  async safeApiCall<T>(apiCall: () => Promise<T>): Promise<ApiResult<T>> {
    try {
      const data = await apiCall();
      return { success: true, data };
    } catch (error) {
      console.error('API call failed:', error);
      const apiError: AnyApiError = {
        code: 'API_ERROR',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString(),
      };
      return { success: false, error: apiError };
    }
  },

  /**
   * Check if API is healthy
   */
  async healthCheck(): Promise<{ status: 'healthy' | 'unhealthy'; timestamp: string }> {
    try {
      // Try a simple request to check API health
      await apiFetch<any>('/health', { method: 'GET' });
      return { status: 'healthy', timestamp: new Date().toISOString() };
    } catch (error) {
      return { status: 'unhealthy', timestamp: new Date().toISOString() };
    }
  },

  /**
   * Get API base URL for external use
   */
  getBaseUrl(): string {
    return API_BASE_URL;
  },
};

// Export the API service as default
export default apiService;

// Also export individual methods for tree-shaking if needed
export const {
  authenticateProvider,
  authenticateProfessional,
  getProviderAppointments,
  getAppointmentDetails,
  createStaffRequest,
  updateAppointment,
  deleteAppointment,
  getMedicalRoles,
  getProviders,
  getProfessionals,
  getProfessionalByNI,
  getRequiredCoursesForProfessional,
  getCourseContent,
  saveTrainingProgress,
  getTrainingProgressForProfessional,
  storeCertificate,
  getConversations,
  createConversation,
  getMessages,
  sendMessage,
  markAsRead,
  setTypingStatus,
  getOnlineUsers,
  safeApiCall,
  healthCheck,
} = apiService;